<template>
  <v-container fluid pa-0 grid-list-md>
    <v-layout row wrap mb-3 v-if="isCurrentPeriod && !loading">
      <v-flex xs12 md6 v-if="!isPlanOpened">
        <v-alert :value="true" type="error" key="alerts_plan_plan_closed">
          {{ $t('alerts.plan_plan_closed') }}
        </v-alert>
      </v-flex>
      <v-flex xs12 md6 v-if="!isValueOpened">
        <v-alert :value="true" type="error" key="alerts_plan_value_closed">
          {{ $t('alerts.plan_value_closed') }}
        </v-alert>
      </v-flex>
      <v-flex xs12 md6 v-if="isPlanOpened">
        <v-alert :value="true" type="warning" v-html="$t('alerts.plan_plan_due_date', { date: planDueDateH })"/>
      </v-flex>
      <v-flex xs12 md6 v-if="isValueOpened">
        <v-alert :value="true" type="warning" v-html="$t('alerts.plan_value_due_date', { date: valueDueDateH } )"/>
      </v-flex>
    </v-layout>
    <v-layout row>
      <v-flex xs12>
        <div v-if="loadingPage" class="text-xs-center mt-4">
          <v-progress-circular :size="80" color="primary" indeterminate/>
        </div>
        <v-data-table v-else class="elevation-1" :headers="headers" :items="indicatorItems" :hide-actions="true"
                      no-data-text="Список индикаторов пуст" :loading="loading">
          <template #items="indicatorRow">
            <!-- category -->
            <category-indicator v-if="indicatorRow.item.type === 'category'"
                                :sequence="indicatorRow.item.sequence"
                                :is-kpi-hidden="isKPIHidden"
                                :title="indicatorRow.item.title[locale]"
                                :count="(categoryTotals[indicatorRow.item.code] ? categoryTotals[indicatorRow.item.code].count : 0)"
                                :count-k="(categoryTotals[indicatorRow.item.code] ? categoryTotals[indicatorRow.item.code].countK : 0)"
                                :award="(categoryTotals[indicatorRow.item.code] ? categoryTotals[indicatorRow.item.code].award : 0)"
                                :award-k="(categoryTotals[indicatorRow.item.code] ? categoryTotals[indicatorRow.item.code].awardK : 0)"
                                :counted="(categoryTotals[indicatorRow.item.code] ? categoryTotals[indicatorRow.item.code].counted : 0)"
                                :counted-k="(categoryTotals[indicatorRow.item.code] ? categoryTotals[indicatorRow.item.code].countedK : 0)"
            />
            <!-- Single indicator -->
            <template v-else-if="!indicatorRow.item.parent && !indicatorRow.item.children">
              <tr>
                <td class="pl-1 pr-0" style="vertical-align: top; text-align: center;"
                    :rowspan="indicatorRow.item.children ? Object.values(indicatorRow.item.children).length + 1 : 1">
                  <v-chip label small dark color="blue-grey">{{
                      categories[indicatorRow.item.category].sequence
                    }}.{{ indicatorRow.item.sequence }}
                  </v-chip>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-icon color="info" v-on="on" style="cursor: pointer;">help</v-icon>
                    </template>
                    <span>{{ $t(`indicators.${indicatorRow.item.id}.description`) }}</span>
                  </v-tooltip>
                </td>
                <td class="subheading pl-1" :colspan="isKPIHidden ? 6 : 7">
                  <b>{{ indicatorRow.item.title[locale] }}</b>
                </td>
                <td>{{ indicatorRow.item.unit[locale] }}</td>
              </tr>
              <tr>
                <td colspan="2"></td>
                <td v-if="!isKPIHidden" class="text-sm-center">
                  <v-tooltip v-if="indicatorRow.item.kpi" top>
                    <template #activator="{ on }">
                      <v-icon color="warning" v-on="on" small style="cursor: pointer;">grade</v-icon>
                    </template>
                    <span>{{ $t('hints.kpi_indicator') }}</span>
                  </v-tooltip>
                </td>
                <td class="text-sm-center">
                  {{ indicatorRow.item.award }}
                </td>
                <td class="text-sm-center">
                  <planned-value-field
                      v-if="indicatorRow.item.planned"
                      :value="plannedValues[indicatorRow.item.id]"
                      :indicator="indicatorRow.item"
                      :readonly="!isPlanOpened"
                      :editable="isPlanOpened"
                      @update="v => plannedValues[indicatorRow.item.id] = v"
                  />
                </td>
                <td class="text-xs-center body-2">
                  {{
                    ((currentValues[indicatorRow.item.id] ? Object.values(currentValues[indicatorRow.item.id].values).length : 0) || 0) | printFloat
                  }}
                </td>
                <td class="text-xs-center body-3">
                  {{
                    ((currentValues[indicatorRow.item.id] ? currentValues[indicatorRow.item.id].award : 0) || 0) | printFloat
                  }}
                </td>
                <td class="text-xs-center body-3">
                  {{
                    ((currentValues[indicatorRow.item.id] ? currentValues[indicatorRow.item.id].counted : '') || '') | printFloat
                  }}
                </td>
                <td>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn fab small color="info" v-on="on" @click="showIndicatorValues(indicatorRow.item)">
                        <v-icon>list</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('hints.view_values') }}</span>
                  </v-tooltip>
                </td>
              </tr>
            </template>
            <!-- Parent indicator -->
            <template v-else-if="!indicatorRow.item.parent && indicatorRow.item.children">
              <tr class="border-top">
                <td class="pl-1 pr-0" style="vertical-align: top; text-align: center;"
                    :rowspan="indicatorRow.item.children ? Object.values(indicatorRow.item.children).length + 1 : 1">
                  <v-chip label small dark color="blue-grey">{{
                      categories[indicatorRow.item.category].sequence
                    }}.{{ indicatorRow.item.sequence }}
                  </v-chip>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-icon color="info" v-on="on" style="cursor: pointer;">help</v-icon>
                    </template>
                    <span>{{ $t(`indicators.${indicatorRow.item.id}.description`) }}</span>
                  </v-tooltip>
                </td>
                <td class="subheading pl-1" :colspan="isKPIHidden ? 6 : 7">
                  <b>{{ $t(`indicators.${indicatorRow.item.id}.title`) }}</b>
                </td>
                <td>{{ $t(`indicators.${indicatorRow.item.id}.unit`) }}</td>
              </tr>
            </template>
            <!-- Child indicator -->
            <template v-else>
              <tr>
                <td class="subheading pl-1">
                  {{ indicatorRow.item.sequence }}) {{ $t(`indicators.${indicatorRow.item.id}.title`) }}
                </td>
                <td v-if="!isKPIHidden" class="text-sm-center">
                  <v-tooltip v-if="indicatorRow.item.kpi" top>
                    <template #activator="{ on }">
                      <v-icon color="warning" v-on="on" small style="cursor: pointer;">grade</v-icon>
                    </template>
                    <span>{{ $t('hints.kpi_indicator') }}</span>
                  </v-tooltip>
                </td>
                <td class="text-sm-center">
                  {{ indicatorRow.item.award }}
                </td>
                <td class="text-sm-center">
                  <planned-value-field
                      v-if="indicatorRow.item.planned"
                      :value="plannedValues[indicatorRow.item.id]"
                      :indicator="indicatorRow.item"
                      :readonly="!isPlanOpened"
                      :editable="isPlanOpened"
                      @update="v => plannedValues[indicatorRow.item.id] = v"
                  />
                </td>
                <td class="text-xs-center body-3">
                  {{
                    ((currentValues[indicatorRow.item.id] ? Object.values(currentValues[indicatorRow.item.id].values).length : 0) || 0) | printFloat
                  }}
                </td>
                <td class="text-xs-center body-3">
                  {{
                    ((currentValues[indicatorRow.item.id] ? currentValues[indicatorRow.item.id].award : 0) || 0) | printFloat
                  }}
                </td>
                <td class="text-xs-center body-3">
                  {{
                    ((currentValues[indicatorRow.item.id] ? currentValues[indicatorRow.item.id].counted : '') || '') | printFloat
                  }}
                </td>
                <td>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn fab small color="info" v-on="on" @click="showIndicatorValues(indicatorRow.item)">
                        <v-icon>list</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('hints.view_values') }}</span>
                  </v-tooltip>
                </td>
              </tr>
            </template>
          </template>
          <template #footer>
            <tr>
              <td rowspan="2" :colspan="isKPIHidden ? 4 : 5" class="text-xs-right font-weight-bold">
                Итоговое значение
              </td>
              <th>Внесено работ</th>
              <th>Предварительный балл</th>
              <th>Засчитаный балл</th>
              <td rowspan="3"></td>
            </tr>
            <tr>
              <td class="text-sm-center font-weight-bold">{{ totals.count }}</td>
              <td class="text-sm-center font-weight-bold">{{ totals.award }}</td>
              <td class="text-sm-center font-weight-bold">{{ totals.counted }}</td>
            </tr>
<!--            <tr>-->
<!--              <td :colspan="isKPIHidden ? 4 : 5" class="text-xs-right font-weight-bold">По ключевым индикаторам</td>-->
<!--              <td class="text-sm-center font-weight-bold">{{ totals.countK }}</td>-->
<!--              <td class="text-sm-center font-weight-bold">{{ totals.awardK }}</td>-->
<!--              <td class="text-sm-center font-weight-bold">{{ totals.countedK }}</td>-->
<!--            </tr>-->
          </template>
        </v-data-table>

        <v-dialog v-model="value.dialog" persistent lazy :max-width="500">
          <v-card>
            <v-card-title>
              <span class="headline">Значения индикатора</span>
            </v-card-title>
            <v-card-text>
              <p v-if="value.readonly" class="red--text">Засчитано, менять не разрешено</p>
              <value-form v-model="value.object" :readonly="value.readonly" @on-validate="value.valid = $event"
                          :fields="indicatorFields(value.object.indicator)"
                          :key="'indicator-value-' + value.object.id"/>
              <v-textarea v-if="hasCommentForValue(value)" outline label="Комментарий"
                          :value="getCommentForValue(value.object)" :rows="1" :readonly="true"
                          :messages="getCommentAuthorForValue(value.object)"/>
            </v-card-text>
            <v-card-actions>
              <v-btn v-if="value.object.indicator.single && value.object.id && !value.readonly" color="warning" flat
                     @click="deleteIndicatorValue(currentIndicator, value.object)">Очистить
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn v-if="!value.readonly" color="primary" flat @click="saveValue" :disabled="!value.valid">Сохранить
              </v-btn>
              <v-btn color="red" flat @click="value.dialog = false">Закрыть</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="valuesList.dialog" persistent lazy :max-width="800">
          <v-card>
            <v-card-title>
              <span class="headline">Значения индикатора</span>
            </v-card-title>
            <v-card-text>
              <v-data-table :headers="headersValue" :items="itemsValue" hide-actions
                            :key="'values-list:' + currentIndicator.id" no-data-text="Еще нет ни одного значения">
                <template v-slot:items="valueRow">
                  <tr>
                    <td v-for="field in headersValue" :key="field.id"
                        :class="{'no-wrap': field.id === 'actions', 'text-xs-center': field.id === 'award'}">
                      <template v-if="field.id === 'actions'">
                        <v-btn flat icon color="primary"
                               @click="showIndicatorValueForm(currentIndicator, valueRow.item.id, valueRow.item.readonly)">
                          <v-icon v-if="valueRow.item.readonly">view_list</v-icon>
                          <v-icon v-else>edit</v-icon>
                        </v-btn>
                        <v-tooltip v-if="valueRow.item.comment" top>
                          <template #activator="{ on }">
                            <v-btn flat icon color="info" v-on="on">
                              <v-icon>info</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ valueRow.item.comment }} <br/>{{ valueRow.item.comment_author }}</span>
                        </v-tooltip>
                        <v-btn flat icon color="error" @click="deleteIndicatorValue(currentIndicator, valueRow.item)"
                               :disabled="valueRow.item.readonly">
                          <v-icon>delete</v-icon>
                        </v-btn>
                      </template>
                      <template v-else-if="field.id === 'award'">
                        {{ valueRow.item.award }}
                      </template>
                      <template v-else>
                        <template
                            v-if="(currentIndicator.parent ? indicators[currentIndicator.parent].children[currentIndicator.id].fields[field.id].type : indicators[currentIndicator.id].fields[field.id].type) === 'enum'">
                          {{
                            (currentIndicator.parent ? indicators[currentIndicator.parent].children[currentIndicator.id].fields[field.id] : indicators[currentIndicator.id].fields[field.id]).enums[valueRow.item.fields[field.id]].value[locale]
                          }}
                        </template>
                        <template v-else>
                          {{ valueRow.item.fields[field.id] }}
                        </template>
                      </template>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
            <v-card-actions>
              <v-spacer/>
              <v-btn color="primary" flat @click="newIndicatorValue(currentIndicator)">Добавить</v-btn>
              <v-btn color="red" flat @click="valuesList.dialog = false">Закрыть</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'

import { sortBySequence, orderByCreatedDate, printFloat } from '@/plugins/helpers'

import { Indicator, IndicatorValue } from '@/api/indicators'

import ValueForm from '@/components/forms/ValueForm'

import CategoryIndicator from '@/components/indicators/Category'
import PlannedValueField from '@/components/forms/PlannedValueField'

// function delay(callback, ms) {
//   let timer = 0;
//   return function () {
//     let context = this, args = arguments;
//     clearTimeout(timer);
//     timer = setTimeout(function () {
//       callback.apply(context, args);
//     }, ms || 0);
//   };
// }

export default {
  components: {
    ValueForm,
    CategoryIndicator,
    PlannedValueField
  },
  filters: {
    printFloat,
    date(d) {
      return d ? d.toLocaleString() : ''
    }
  },
  data: (vm) => ({
    closed: false,
    refreshTimer: null,
    loadingPage: true,
    loading: false,
    currentIndicator: new Indicator(),
    value: {
      dialog: false,
      valid: false,
      readonly: false,
      object: new IndicatorValue({ period: vm.$route.params.period })
    },
    valuesList: {
      dialog: false
    },
    categoryTotals: {},
    totals: {
      award: 0,
      counted: 0
    },
    plannedValues: {},
    plannedValuesA: [],
    savingIndicatorPlan: ''
  }),
  computed: {
    locale() {
      return this.$i18n.locale
    },
    period() {
      return this.$route.params.period
    },
    isKPIHidden() {
      return this.period === '2022-2023';
    },
    isCurrentPeriod() {
      return this.period === this.$store.getters.settings['current-period']
    },
    planDueDate() {
      return this.$store.getters.settings['plan-due-date'] ? new Date(this.$store.getters.settings['plan-due-date']) : null
    },
    planDueDateH() {
      return this.planDueDate ? this.planDueDate.toLocaleString() : ''
    },
    valueDueDate() {
      return this.$store.getters.settings['value-due-date'] ? new Date(this.$store.getters.settings['value-due-date']) : null
    },
    valueDueDateH() {
      return this.valueDueDate ? this.valueDueDate.toLocaleString() : ''
    },
    isPlanOpened() {
      if (!this.isCurrentPeriod) {
        return false
      }
      if (!this.planDueDate) {
        return true
      }

      const dt = new Date()

      return dt < this.planDueDate
    },
    isValueOpened() {
      if (!this.isCurrentPeriod) {
        return false
      }

      if (!this.valueDueDate) {
        return true
      }

      const dt = new Date()

      return dt < this.valueDueDate
    },
    ...mapState('indicators', {
      categories: (state) => state.categories,
      indicators: (state) => state.indicators,
      values: (state) => state.values
    }),
    ...mapState('periods', {
      periods: state => state.periods
    }),
    headers() {
      let headers = [{
        text: '',
        sortable: false,
        value: null
      }, {
        text: this.$t('headers.indicator'),
        align: 'left',
        sortable: false,
        value: 'title'
      }];

      if (!this.isKPIHidden) {
        headers.push({
          text: this.$t('headers.kpi_indicator'),
          align: 'center',
          class: 'multiline',
          sortable: false,
          value: null,
        })
      }

      headers = [...headers, ...[
        {
          text: this.$t(this.isKPIHidden ? 'headers.award_' : 'headers.award'),
          align: 'center',
          class: 'multiline',
          sortable: false,
          value: null
        },
        {
          text: this.$t('headers.plan'),
          align: 'left',
          sortable: false,
          value: null
        },
        {
          text: this.$t('headers.submitted'),
          sortable: false,
          align: 'center',
          class: 'multiline',
          value: null
        },
        {
          text: this.$t(this.isKPIHidden ? 'headers.award_pre_' : 'headers.award_pre'),
          sortable: false,
          align: 'center',
          class: 'multiline',
          value: null
        },
        {
          text: this.$t(this.isKPIHidden ? 'headers.award_counted_' : 'headers.award_counted'),
          sortable: false,
          align: 'center',
          class: 'multiline',
          value: null
        },
        {
          text: '',
          sortable: false,
          value: null
        },
        // {
        //   text: 'Единицы измерения',
        //   align: 'left',
        //   sortable: false,
        //   value: null
        // }
      ]]

      return headers
    },
    indicatorItems() {
      let items = []

      Object.values(this.categories).sort(sortBySequence).forEach((category) => {
        const indicators = [];
        Object.values(this.indicators).filter((indicator) => {
          return indicator.category === category.code
        }).sort(sortBySequence).forEach((indicator) => {
          indicators.push({ ...indicator, type: 'indicator' })

          if (indicator.children) {
            Object.values(indicator.children).sort(sortBySequence).forEach((child) => {
              indicators.push({ ...child, type: 'indicator' })
            })
          }
        })
        if (indicators.length > 0) {
          items.push({ ...category, type: 'category' })
          items.push(...indicators);
        }
      })

      return items
    },
    currentValues() {
      return this.values[this.$route.params.period] || {}
    },

    headersValue() {
      if (this.currentIndicator.id === '') return []
      let items = []

      if (!this.currentIndicator.parent) {
        if (!this.indicators[this.currentIndicator.id] || !this.indicators[this.currentIndicator.id].fields) {
          return []
        }
        items = Object.values(this.indicators[this.currentIndicator.id].fields).sort(sortBySequence).filter((field) => {
          return field.listed
        }).map((field) => {
          return {
            id: field.id,
            text: field.title[this.locale],
            value: null,
            sortable: false,
          }
        })

        items.push({ id: 'award', text: 'Балл', value: null, sortable: false, align: 'center', width: 120 })
        items.push({ id: 'actions', text: '', value: null, sortable: false, width: 152 })
      } else {
        if (!this.indicators[this.currentIndicator.parent].children[this.currentIndicator.id] || !this.indicators[this.currentIndicator.parent].children[this.currentIndicator.id].fields) {
          return []
        }
        items = Object.values(this.indicators[this.currentIndicator.parent].children[this.currentIndicator.id].fields).sort(sortBySequence).filter((field) => {
          return field.listed
        }).map((field) => {
          return {
            id: field.id,
            text: field.title[this.locale],
            value: null,
            sortable: false,
          }
        })

        items.push({ id: 'award', text: 'Балл', value: null, sortable: false, align: 'center', width: 120 })
        items.push({ id: 'actions', text: '', value: null, sortable: false, width: 152 })
      }

      return items
    },
    itemsValue() {
      if (this.currentIndicator.id === '') return []

      let values = []
      if (!this.currentIndicator.parent) {
        if (!this.indicators[this.currentIndicator.id] || !this.indicators[this.currentIndicator.id].fields) {
          return []
        }
        values = Object.values(this.currentValues[this.currentIndicator.id].values).sort(orderByCreatedDate)
      } else {
        if (!this.indicators[this.currentIndicator.parent].children[this.currentIndicator.id] || !this.indicators[this.currentIndicator.parent].children[this.currentIndicator.id].fields) {
          return []
        }
        values = Object.values(this.currentValues[this.currentIndicator.id].values).sort(orderByCreatedDate)
      }

      return values
    }
  },
  watch: {
    period() {
      this.refresh()
    }
  },
  mounted() {
    this.refresh()
  },
  methods: {
    hasCommentForValue(value) {
      return !!this.getCommentForValue(value)
    },
    getCommentForValue(value) {
      if (!this.currentValues || !this.currentIndicator.id || !this.currentValues[this.currentIndicator.id] || !value.id || !this.currentValues[this.currentIndicator.id].values[value.id]) {
        return ''
      }
      return this.currentValues[this.currentIndicator.id].values[value.id].comment
    },
    getCommentAuthorForValue(value) {
      if (!this.currentValues || !this.currentIndicator.id || !this.currentValues[this.currentIndicator.id] || !value.id || !this.currentValues[this.currentIndicator.id].values[value.id]) {
        return ''
      }
      return this.currentValues[this.currentIndicator.id].values[value.id].comment_author
    },
    updateTotals() {
      this.categoryTotals = {}
      this.totals = {
        count: 0,
        countK: 0,
        award: 0,
        awardK: 0,
        counted: 0,
        countedK: 0
      }
      Object.keys(this.currentValues).forEach((val_id) => {
        let cat_id
        if (!this.currentValues[val_id].parent) {
          cat_id = this.indicators[val_id].category
        } else {
          cat_id = this.indicators[this.currentValues[val_id].parent]?.category
        }

        this.plannedValues = Object.assign({}, this.plannedValues, { [val_id]: this.currentValues[val_id].plan })

        if (!this.categoryTotals[cat_id]) {
          this.categoryTotals = Object.assign(this.categoryTotals, {
            [cat_id]: {
              count: 0,
              award: 0,
              counted: 0,
              countK: 0,
              awardK: 0,
              countedK: 0
            }
          })
        }

        this.categoryTotals[cat_id].count += Object.values(this.currentValues[val_id].values).length
        this.categoryTotals[cat_id].award += this.currentValues[val_id].award
        this.categoryTotals[cat_id].counted += this.currentValues[val_id].counted

        this.totals.count += Object.values(this.currentValues[val_id].values).length
        this.totals.award += this.currentValues[val_id].award
        this.totals.counted += this.currentValues[val_id].counted

        if (this.currentValues[val_id].kpi) {
          this.categoryTotals[cat_id].countK += Object.values(this.currentValues[val_id].values).length
          this.categoryTotals[cat_id].awardK += this.currentValues[val_id].award
          this.categoryTotals[cat_id].countedK += this.currentValues[val_id].counted

          this.totals.countK += Object.values(this.currentValues[val_id].values).length
          this.totals.awardK += this.currentValues[val_id].award
          this.totals.countedK += this.currentValues[val_id].counted
        }
      })

      Object.keys(this.plannedValues).forEach(id => {
        if (!id.startsWith('=')) {
          this.plannedValuesA.push({
            id,
            val: this.plannedValues[id]
          })
        }
      })
    },
    async loadSettings() {
      const options = [
        'current-period',
        'plan-due-date',
        'value-due-date'
      ]
      await this.$store.dispatch('loadSettings', { options })
    },
    loadIndicators() {
      this.loading = true
      this.loadingPage = true
      this.$store.dispatch('indicators/getIndicators', this.period).then(() => {
        this.loadValues()

        const messagesRu = {
          categories: {},
          indicators: {}
        }
        const messagesKz = {
          categories: {},
          indicators: {}
        }

        for (let categoryID in this.categories) {
          const category = this.categories[categoryID]
          messagesRu.categories[categoryID] = {
            title: category.title.ru,
            abbreviation: category.abbreviation.ru
          }
          messagesKz.categories[categoryID] = {
            title: category.title.kz,
            abbreviation: category.abbreviation.kz
          }
        }

        for (let indicatorID in this.indicators) {
          const indicator = this.indicators[indicatorID]
          messagesRu.indicators[indicatorID] = {
            title: indicator.title.ru,
            description: indicator.description.ru,
            unit: indicator.unit.ru,
          }
          messagesKz.indicators[indicatorID] = {
            title: indicator.title.kz,
            description: indicator.description.kz,
            unit: indicator.unit.kz,
          }

          if (indicator.children) {
            for (let childID in indicator.children) {
              if (!indicator.children.hasOwnProperty(childID)) {
                continue
              }
              const child = indicator.children[childID]
              messagesRu.indicators[childID] = {
                title: child.title.ru,
                description: child.description.ru,
                unit: child.unit.ru,
              }
              messagesKz.indicators[childID] = {
                title: child.title.kz,
                description: child.description.kz,
                unit: child.unit.kz,
              }
            }
          }
        }

        this.$i18n.mergeLocaleMessage('ru', messagesRu)
        this.$i18n.mergeLocaleMessage('kz', messagesKz)
      }).catch((error) => {
        this.$events.$emit('notify', {
          message: error.message,
          type: 'error'
        })
      })
    },
    loadValues(indicator) {
      this.$store.dispatch('indicators/getValues', {
        period: this.periods[this.$route.params.period],
        indicator
      }).then(() => {
        this.updateTotals()
        this.loading = false
        this.loadingPage = false
        this.$events.$emit('notify', {
          message: 'Данные загружены',
          type: 'info'
        })
      }).catch((error) => {
        // eslint-disable-next-line
        console.error(error)
        this.$events.$emit('notify', {
          message: error.message,
          type: 'error'
        })
      })
    },
    async refresh() {
      await this.loadSettings()
      this.loadIndicators()
    },
    newIndicatorValue(indicator) {
      if (indicator.id === '') {
        return
      }
      this.value.readonly = false
      this.value.object = new IndicatorValue({ period: this.period })

      this.value.object.period = this.$route.params.period
      this.value.object.indicator = new Indicator(indicator)
      this.value.dialog = true
    },
    showIndicatorValueForm(indicator, value_id, readonly) {
      readonly = readonly || false
      this.value.object = new IndicatorValue({ period: this.period })

      if (this.currentValues[indicator.id].values[value_id]) {
        const value = this.currentValues[indicator.id].values[value_id]
        this.value.object.id = value.id
        this.value.readonly = value.readonly || readonly
        this.value.object.values = Object.assign({}, this.value.object.values, value.fields)
      } else {
        this.value.readonly = false
        this.value.object = new IndicatorValue({ period: this.period })
      }

      this.value.object.period = this.$route.params.period
      this.value.object.indicator = new Indicator(indicator)
      this.value.dialog = true
    },
    showIndicatorValuesList() {
      this.valuesList.dialog = true
    },
    showIndicatorValues(indicator) {
      this.$store.dispatch('indicators/getFieldsForIndicator', indicator).then(() => {
        this.currentIndicator = indicator
        if (indicator.single) {
          let value_id = (Object.keys(this.currentValues[indicator.id].values).length > 0) ? Object.keys(this.currentValues[indicator.id].values)[0] : ''
          this.showIndicatorValueForm(indicator, value_id)
        } else {
          this.showIndicatorValuesList()
        }
      }).catch((error) => {
        this.$events.$emit('notify', {
          message: error.message,
          type: 'error'
        })
        this.currentIndicator = new Indicator()
      })
    },
    deleteIndicatorValue(indicator, value) {
      this.$events.$emit('confirm', {
        message: 'Вы действительно хотите удалить данное значение?',
        action: () => {
          this.$store.dispatch('indicators/deleteValue', { period: this.period, indicator, value }).then(() => {
            this.value.object = new IndicatorValue({ period: this.period, indicator })
            this.loadValues(indicator)
          }).catch((error) => {
            this.$events.$emit('notify', {
              message: error.message,
              type: 'error'
            })
            setTimeout(() => {
              this.loadValues(indicator)
            }, 3000)
          }).finally(() => {
          })
        }
      })
    },

    indicatorFields(indicator) {
      if (!indicator.parent) {
        if (!this.indicators[indicator.id]) {
          return []
        }
        return Object.values(this.indicators[indicator.id].fields).sort(sortBySequence)
      } else {
        if (!this.indicators[indicator.parent].children[indicator.id] || !this.indicators[indicator.parent].children[indicator.id].fields) {
          return []
        }
        return Object.values(this.indicators[indicator.parent].children[indicator.id].fields).sort(sortBySequence)
      }
    },
    saveValue() {
      this.$store.dispatch('indicators/saveValue', this.value.object).then(() => {
        this.$events.$emit('notify', {
          message: 'Значения сохранены',
          type: 'info'
        })
        this.$nextTick(() => {
          this.loadValues(this.value.object.indicator)
        })
      }).catch((error) => {
        this.$events.$emit('notify', {
          message: error.message,
          type: 'error'
        })
        setTimeout(() => {
          this.loadValues(this.value.object.indicator)
        }, 3000)
      }).finally(() => {
        this.value.dialog = false
      })
    },
    async saveIndicatorPlan(indicator) {
      this.savingIndicatorPlan = indicator.id
      try {
        await this.$store.dispatch('indicators/savePlan', {
          period: this.period,
          indicator,
          plannedValue: this.plannedValues[indicator.id]
        })
        this.plannedValues['=' + indicator.id] = this.plannedValues[indicator.id]
        this.plannedValues[':' + indicator.id] = false
        this.$events.$emit('notify', {
          message: 'Плановое значение сохранено',
          type: 'info'
        })
      } catch (error) {
        this.$events.$emit('notify', {
          message: error.message,
          type: 'error'
        })
      }
      this.savingIndicatorPlan = ''
    }
  }
}
</script>

<style>
.input-plan input {
  border: 1px solid gray;
  min-width: 32px;
  max-width: 48px;
}

.input-plan input:read-only {
  border: none;
}

.input-plan .v-input__append-outer {
  margin-top: 0;
  margin-left: 4px;
}

table.v-table thead th.multiline {
  white-space: normal;
}

.theme--light.v-table tbody tr.category {
  /*border-top: 2px solid;*/
  border-top-width: 4px;
  border-top-color: #2196f3;
}

.theme--light.v-table tbody tr.border-top {
  /*border-top: 2px solid;*/
  border-top-width: 4px;
}
</style>

<i18n>
{
  "ru": {
    "headers": {
      "indicator": "Индикатор",
      "kpi_indicator": "Ключевой индикатор",
      "award": "Баллы за единицу",
      "award_": "Баллы за единицу",
      "plan": "План",
      "submitted": "Количество работ",
      "award_pre": "Предварительный балл",
      "award_pre_": "Предварительный балл",
      "award_counted": "Засчитанный балл",
      "award_counted_": "Засчитанный балл"
    },
    "hints": {
      "kpi_indicator": "Ключевой индикатор",
      "view_values": "Просмотр значений индикатора"
    },
    "alerts": {
      "plan_value_closed": "Внимание! Индикативный план закрыт. Внесение и изменение значений заблокировано!",
      "plan_plan_closed": "Внимание! Внесение плановых значений закрыто!",
      "plan_plan_due_date": "Внимание! Внесение плановых значений доступно до&nbsp;<b><u>{date}</u></b>",
      "plan_value_due_date": "Внимание! Внесение значений инидкаторов доступно до&nbsp;<b><u>{date}</u></b>"
    }
  },
  "kz": {
    "headers": {
      "indicator": "Көрсеткіш",
      "kpi_indicator": "Кілттік көрсеткіш",
      "award": "Бірлік үшін бағалау ұпайлары",
      "award_": "Бірлік үшін ұпайлары",
      "plan": "Жоспар",
      "submitted": "Орындалған жұмыс саны",
      "award_pre": "Алдын ала балл",
      "award_pre_": "Алдын ала балл",
      "award_counted": "Балл",
      "award_counted_": "Балл"
    },
    "hints": {
      "kpi_indicator": "Кілттік көрсеткіш",
      "view_values": "Көрсеткіштің мәні"
    },
    "alerts": {
      "plan_value_closed": "Назар аударыңыз! Индикативті жоспар жабылды. Мәліметтерді енгізуге және өзгертуге тыйым салынды!",
      "plan_plan_closed": "Назар аударыңыз! Жоспарланған мәліметтерді енгізуге тыйым салынды!",
      "plan_plan_due_date": "Назар аударыңыз! Жоспарланған мәліметтерді&nbsp;<b><u>{date}</u></b>&nbsp;дейін енгізуге болады",
      "plan_value_due_date": "Назар аударыңыз! Мәліметтерді&nbsp;<b><u>{date}</u></b>&nbsp;дейін енгізуге болады"
    }
  }
}
</i18n>
